import OpenAI from 'openai'
import { parseJsonObject } from './json'
import type { ClientOpts, LlmClient, LlmResult, StructuredRequest } from './types'

/**
 * OpenAI / OpenAI-compatible client over the Chat Completions API — the universal
 * endpoint every compatible provider implements (OpenRouter, Groq, DeepSeek,
 * Together, Fireworks, xAI, Gemini-compat, Ollama). `baseURL` selects the
 * endpoint; `headers` are sent on every request (a header-auth gateway's
 * credentials); `provider` is the name self-cost pricing keys on.
 *
 * Structured output is one forced function call whose arguments ARE the result,
 * so the model must support tool calling. `strict` is NOT set: many compatible
 * providers do function calling but not strict json-schema, and enrich-session
 * normalizes defensively anyway. Token cap uses `max_completion_tokens` (native
 * gpt-5/o-series require it; compatible endpoints accept it — verified on
 * Ollama/Groq/Gemini).
 *
 * Model choice matters: pick one that doesn't burn the token budget on hidden
 * reasoning before emitting the tool call. The default `gpt-5.4-mini` doesn't
 * reason on this Chat-Completions+tools path (reasoning there is gated to the
 * Responses API), so it never starves. An older reasoning-by-default model like
 * `gpt-5-mini` can exhaust the budget reasoning and return empty output on large
 * sessions — prefer the default.
 */
export function createOpenAiClient(apiKey: string, model: string, opts?: ClientOpts): LlmClient {
  const client = new OpenAI({ apiKey, baseURL: opts?.baseURL, defaultHeaders: opts?.headers })
  return {
    provider: opts?.provider ?? 'openai',
    model,
    async completeStructured(req: StructuredRequest): Promise<LlmResult> {
      const { system, user, schema, toolName, maxTokens = 1024 } = req
      const resp = await client.chat.completions.create({
        model,
        max_completion_tokens: maxTokens,
        messages: [
          { role: 'system', content: system },
          { role: 'user', content: user },
        ],
        tools: [{ type: 'function', function: { name: toolName, description: 'Record the structured analysis.', parameters: schema } }],
        tool_choice: { type: 'function', function: { name: toolName } },
      })
      const msg = resp.choices[0]?.message
      const call = msg?.tool_calls?.find((c) => c.type === 'function' && c.function.name === toolName)
      if (call?.type === 'function') return { data: parseJsonObject(call.function.arguments) ?? {}, usage: usageOf(resp.usage) }
      // No tool call came back; salvage any plain-text JSON.
      return { data: parseJsonObject(msg?.content ?? '') ?? {}, usage: usageOf(resp.usage) }
    },
  }
}

function usageOf(u: OpenAI.Completions.CompletionUsage | undefined) {
  // cache_write_tokens was introduced after the SDK's current usage type.
  const details = u?.prompt_tokens_details as { cached_tokens?: number; cache_write_tokens?: number } | undefined
  const cached = details?.cached_tokens ?? 0
  const cacheWrite = details?.cache_write_tokens ?? 0
  return {
    input: Math.max(0, (u?.prompt_tokens ?? 0) - cached - cacheWrite),
    output: u?.completion_tokens ?? 0,
    cacheCreate5m: cacheWrite,
    cacheCreate1h: 0,
    cacheRead: cached,
  }
}
